import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string) {
    super(`API error ${status}: ${body.slice(0, 200)}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiClientOptions {
  /** Base URL for the API (no trailing slash). e.g. `https://api.example/dev` */
  baseUrl: string;
  /** Live MSAL instance — used to acquire access tokens silently. */
  msal: IPublicClientApplication;
  /** The signed-in account; passed to `acquireTokenSilent`. */
  account: AccountInfo;
  /** API audience (Application ID URI). The token request asks for `<audience>/.default`. */
  apiAudience: string;
  /** Inject `fetch` for tests. Defaults to `globalThis.fetch`. */
  fetchFn?: typeof fetch;
}

/**
 * Thin Bearer-token-attaching `fetch` wrapper.
 *
 * Reads the active MSAL account, silently acquires an access token, and
 * sends it as `Authorization: Bearer <token>` on every request. Non-2xx
 * responses throw {@link ApiError} with the original status + body.
 *
 * Token acquisition errors propagate to the caller — `acquireTokenSilent`
 * fails on missing accounts or expired sessions, and we let TanStack
 * Query / the page layer surface those.
 */
export class ApiClient {
  private readonly fetchFn: typeof fetch;

  constructor(private readonly options: ApiClientOptions) {
    this.fetchFn = options.fetchFn ?? globalThis.fetch.bind(globalThis);
  }

  get<T>(path: string): Promise<T> {
    return this.request('GET', path);
  }

  post<T>(path: string, body: unknown): Promise<T> {
    return this.request('POST', path, body);
  }

  put<T>(path: string, body: unknown): Promise<T> {
    return this.request('PUT', path, body);
  }

  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request('PATCH', path, body);
  }

  delete<T>(path: string): Promise<T> {
    return this.request('DELETE', path);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.acquireToken();
    const res = await this.fetchFn(`${this.options.baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      throw new ApiError(res.status, await res.text());
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  private async acquireToken(): Promise<string> {
    const result = await this.options.msal.acquireTokenSilent({
      scopes: [`${this.options.apiAudience}/.default`],
      account: this.options.account,
    });
    return result.accessToken;
  }
}
